
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';

export interface UserProfile {
  name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
}

export const useUserName = (): string => {
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error("Erreur lors de la récupération des données utilisateur:", userError);
          return;
        }
        
        if (userData.user) {
          // Essayer de récupérer le nom complet depuis les métadonnées
          const fullName = userData.user.user_metadata?.full_name || 
                         userData.user.user_metadata?.name ||
                         userData.user.user_metadata?.user_name;
          
          if (fullName) {
            setUserName(fullName);
          } else {
            // Si pas de nom complet, utiliser l'email
            setUserName(userData.user.email || "Utilisateur");
          }
        }
      } catch (error) {
        console.error("Exception lors de la récupération du nom d'utilisateur:", error);
      }
    };

    fetchUserName();

    // Abonner aux changements d'authentification
    const { data: authListener } = supabase.auth.onAuthStateChange(() => {
      fetchUserName();
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  return userName;
};

// Export un hook pour récupérer le profil utilisateur complet
export const useUserProfile = (): UserProfile => {
  const [profile, setProfile] = useState<UserProfile>({
    name: "",
    email: "",
    phone: "",
    avatar_url: ""
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error("Erreur lors de la récupération des données utilisateur:", userError);
          return;
        }
        
        if (userData.user) {
          const fullName = userData.user.user_metadata?.full_name || 
                         userData.user.user_metadata?.name ||
                         userData.user.user_metadata?.user_name ||
                         "Utilisateur";
          
          const phone = userData.user.user_metadata?.phone || "";
          const avatar_url = userData.user.user_metadata?.avatar_url || "";
          
          setProfile({
            name: fullName,
            email: userData.user.email || "",
            phone,
            avatar_url
          });
        }
      } catch (error) {
        console.error("Exception lors de la récupération du profil utilisateur:", error);
      }
    };

    fetchUserProfile();

    // Abonner aux changements d'authentification
    const { data: authListener } = supabase.auth.onAuthStateChange(() => {
      fetchUserProfile();
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  return profile;
};
